import { useSessionContext } from "ui/context/SessionContext";

interface HookReturn {
    hasRightToConfigureShare: boolean;
    hasFeatureConfigureShare: boolean;
    hasRightToPublish: boolean;
    hasFeaturePublish: boolean;
    hasRightToShare: boolean;
    hasFeatureShare: boolean;
    hasRightToCreateFragment: boolean;
    hasFeatureCreateFragment: boolean;
    hasRightToUploadFragment: boolean;
    hasFeatureUploadFragment: boolean;
    hasRightToEditFragment: boolean;
    hasFeatureEditFragment: boolean;
    hasRightToCreateCompilation: boolean;
    hasFeatureCreateCompilation: boolean;
    canConfigureShare: boolean;
    canPublish: boolean;
    canShare: boolean;
    canCreateFragment: boolean;
    canUploadFragment: boolean;
    canEditFragment: boolean;
    canCreateCompilation: boolean;
    canCreateCaptions: boolean;
    canDeleteFragment: boolean;
    canManageAddons: boolean;
    canWatchLive: boolean;
    canCreateVariants: boolean;
    isProjectAdmin: boolean;
}
export default function usePermissions(customUserRoles?: string[]): HookReturn {
    let { userRoles } = useSessionContext();
    const { selectedProject } = useSessionContext();
    if (customUserRoles) {
        userRoles = customUserRoles;
    }
    const projectKey = selectedProject?.key;

    const permissionsCheck = (specificRole: string) => {
        if (!userRoles || userRoles.length <= 0) {
            return false;
        }
        if (
            !userRoles.some(
                (role) =>
                    role === "admin" ||
                    role === "Admin" ||
                    role === specificRole,
            )
        ) {
            return false;
        }
        return true;
    };

    // Specific checks
    const hasRightToConfigureShare: boolean =
        permissionsCheck(
            `UI_PublishShareInitConfigure.ss_publishsettings.ss_project_${projectKey}`,
        ) &&
        permissionsCheck(
            `UI_PublishShareConfigure.ss_publishsettings.ss_project_${projectKey}`,
        );

    const hasFeatureConfigureShare: boolean = permissionsCheck(
        `Feature_PublishShare.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToPublish: boolean = permissionsCheck(
        `UI_Publish.ss_fragments.ss_project_${projectKey}`,
    );
    const hasFeaturePublish: boolean = permissionsCheck(
        `Feature_Publish.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToShare: boolean = permissionsCheck(
        `UI_PublishShare.ss_fragments.ss_project_${projectKey}`,
    );
    const hasFeatureShare: boolean = permissionsCheck(
        `Feature_PublishShare.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToCreateFragment: boolean = permissionsCheck(
        `UI_CreateFromSelections.ss_fragments.ss_project_${projectKey}`,
    );
    const hasFeatureCreateFragment: boolean = permissionsCheck(
        `Feature_CreateFromSelections.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToUploadFragment: boolean = permissionsCheck(
        `UI_CreateFromFile.ss_fragments.ss_project_${projectKey}`,
    );
    const hasFeatureUploadFragment: boolean = permissionsCheck(
        `Feature_CreateFromFile.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToEditFragment: boolean = permissionsCheck(
        `UI_CreateEdit.ss_fragments.ss_project_${projectKey}`,
    );
    const hasFeatureEditFragment: boolean = permissionsCheck(
        `Feature_CreateEdit.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToCreateCompilation: boolean = permissionsCheck(
        `UI_CreateCompilation.ss_fragments.ss_project_${projectKey}`,
    );
    const hasFeatureCreateCompilation: boolean = permissionsCheck(
        `Feature_CreateCompilation.ss_fragments.ss_project_${projectKey}`,
    );
    const hasFeatureCreateCaptions: boolean = permissionsCheck(
        `Feature_Caption.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToDeleteFragment: boolean = permissionsCheck(
        `UI_Delete.ss_fragments.ss_project_${projectKey}`,
    );
    const hasRightToManageAddons: boolean = permissionsCheck(
        `UI_Manage.ss_mediafiles.ss_project_${projectKey}`,
    );
    const hasFeatureWatchLive: boolean = permissionsCheck(
        `Feature_WatchLive.ss_channels.ss_project_${projectKey}`,
    );
    const hasFeatureCreateVariants: boolean = permissionsCheck(
        `Feature_CreateVariant.ss_fragments.ss_project_${projectKey}`,
    );
    const hasAdminRights: boolean = permissionsCheck(
        `UI_Manage.ss_project_${projectKey}`,
    );

    // Combined checks
    const canConfigureShare: boolean =
        hasRightToConfigureShare && hasFeatureConfigureShare;
    const canPublish: boolean = hasRightToPublish && hasFeaturePublish;
    const canShare: boolean = hasRightToShare && hasFeatureShare;
    const canCreateFragment: boolean =
        hasRightToCreateFragment && hasFeatureCreateFragment;
    const canUploadFragment: boolean =
        hasRightToUploadFragment && hasFeatureUploadFragment;
    const canEditFragment: boolean =
        hasRightToEditFragment && hasFeatureEditFragment;
    const canCreateCompilation: boolean =
        hasRightToCreateCompilation && hasFeatureCreateCompilation;
    const canCreateCaptions: boolean = hasFeatureCreateCaptions;

    const canDeleteFragment: boolean = hasRightToDeleteFragment;
    const canManageAddons: boolean = hasRightToManageAddons;
    const canWatchLive: boolean = hasFeatureWatchLive;
    const canCreateVariants: boolean = hasFeatureCreateVariants;
    const isProjectAdmin: boolean = hasAdminRights;

    return {
        // Specific checks
        hasRightToConfigureShare,
        hasFeatureConfigureShare,
        hasRightToPublish,
        hasFeaturePublish,
        hasRightToShare,
        hasFeatureShare,
        hasRightToCreateFragment,
        hasFeatureCreateFragment,
        hasRightToUploadFragment,
        hasFeatureUploadFragment,
        hasRightToEditFragment,
        hasFeatureEditFragment,
        hasRightToCreateCompilation,
        hasFeatureCreateCompilation,

        // Combined Checks
        canConfigureShare,
        canPublish,
        canShare,
        canCreateFragment,
        canUploadFragment,
        canEditFragment,
        canCreateCompilation,
        canCreateCaptions,
        canDeleteFragment,
        canManageAddons,
        canWatchLive,
        canCreateVariants,
        isProjectAdmin,
    };
}
